/**
 * Карантин хозяйства визуализацией экрана «Карантин», суженной до своей
 * земли (agroai-6al.37):
 *
 * * карта ТОЛЬКО контуров хозяйства по статусу (красный — критичный,
 *   жёлтый — наблюдение; зелёный по умолчанию — «событий не было»);
 * * ползунок даты под картой — история статусов: выбранная дата перечитывает
 *   `/farms/{id}/quarantine?as_of=` (статусы считает сервер тем же правилом,
 *   что метрики `quarantine.*`), дата экрана при этом не меняется;
 * * рядом — очаги: контуры в красном и жёлтом с причиной и сроком
 *   предписания, «на карте» — фокус, номер — карточка контура на вкладке
 *   карантина (туда же ведёт карточка контура с карты).
 *
 * Ниже — статусы по месяцам, находки с предписаниями и сроками, события.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ShieldCheck } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { request, type Schemas } from '../../../api/client';
import { defineMessages, type PlainMessage } from '../../../i18n/define';
import { UNIT_LABELS, useT } from '../../../i18n/hooks';
import { useRequest } from '../../../api/useRequest';
import { Card, Grid, Stack } from '../../ui/Card';
import { KpiCard } from '../../ui/KpiCard';
import { Badge } from '../../ui/Badge';
import { DataTable, type Column } from '../../ui/DataTable';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { LoadError, failed } from '../../ui/LoadError';
import { ActiveFilter, type ActiveFilterItem } from '../../ui/ActiveFilter';
import { ChartContainer, ChartLegend, ChartTooltipContent, niceTicks, type ChartConfig } from '../../ui/chart/Chart';
import { AgroMap } from '../../map/MapWidget';
import type { MapTimelineProps } from '../../map/MapTimeline';
import { useDataSource } from '../../data-source';
import type { QuarantineStatusValue } from '../../data/types';
import { countOf, dateShort, num, WORDS } from '../../lib/format';
import { weekAlignedFrom } from '../quarantineView';
import { FOLLOWUP_LABEL, Q_DIRECTION_LABEL, Q_STATUS_LABEL, REQ_STATE_LABEL, type FarmQuarantine } from './model';
import { LegendFilter, toggled } from './parts';
import type { FarmCabinetData } from './useFarmCabinet';
import { QuarantineSkeleton } from './CabinetSkeleton';
import { findingWorkText } from '../quarantine/findingWork';
import { PrescriptionsWidget } from '../../widgets/prescriptions/PrescriptionsWidget';
import { metricMap, metricNumber, type MetricValue } from './model';
import { CASE_STATE_WORD, CLASS_LONG, OUTSIDE_CYCLE_WORD, haText, landHaNum, partNote, type ObjectClass } from '../quarantine/quarantineArea';

type QFinding = Schemas['QFindingViewOut'];
type QStatus = 'red' | 'yellow' | 'green';

const m = defineMessages({
  chYellow: { id: 'farm.quarantine.chart.yellow', defaultMessage: 'Жёлтый (наблюдение)', description: 'Подпись серии в легенде и подсказке графика «Контуры в жёлтом и красном» на вкладке «Карантин» хозяйства' },
  chRed: { id: 'farm.quarantine.chart.red', defaultMessage: 'Красный', description: 'Подпись серии в легенде и подсказке графика «Контуры в жёлтом и красном»; красный карантинный статус' },
  chInspections: { id: 'farm.quarantine.chart.inspections', defaultMessage: 'Обследования', description: 'Подпись серии в легенде и подсказке графика обследований по месяцам (вкладка «Карантин»)' },
  contourWord: { id: 'farm.quarantine.contourWord', defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}', description: 'Слово «контур» без числа, согласуется с числом: подпись рядом с числом в легенде карты карантина' },
  findingWord: { id: 'farm.quarantine.findingWord', defaultMessage: '{count, plural, one {находка} few {находки} many {находок} other {находки}}', description: 'Слово «находка» без числа — знаменатель под таблицей находок (вкладка «Карантин»)' },
  stRed: { id: 'farm.quarantine.status.red', defaultMessage: 'Красный', description: 'Карантинный статус (цвет) в легенде карты и на KPI; с заглавной' },
  stRedNote: { id: 'farm.quarantine.status.redNote', defaultMessage: 'критичное состояние: назначает человек', description: 'Пояснение к красному статусу в легенде карты карантина хозяйства' },
  stYellow: { id: 'farm.quarantine.status.yellow', defaultMessage: 'Жёлтый', description: 'Карантинный статус (цвет) в легенде карты и на KPI; с заглавной' },
  stYellowNote: { id: 'farm.quarantine.status.yellowNote', defaultMessage: 'под наблюдением', description: 'Пояснение к жёлтому статусу в легенде карты карантина хозяйства' },
  stGreen: { id: 'farm.quarantine.status.green', defaultMessage: 'Зелёный', description: 'Карантинный статус (цвет) в легенде карты; с заглавной' },
  stGreenNote: { id: 'farm.quarantine.status.greenNote', defaultMessage: 'событий нет или очаг снят', description: 'Пояснение к зелёному статусу в легенде карты карантина хозяйства' },
  timelineLabel: { id: 'farm.quarantine.timeline', defaultMessage: 'Карантин на', description: 'Подпись ползунка даты под картой карантина хозяйства; после неё стоит дата' },
  filterAt: { id: 'farm.quarantine.filter.at', defaultMessage: 'статусы на {date}', description: 'Чип активного фильтра над вкладкой «Карантин»: показаны статусы на выбранную неделю; {date} — дата' },
  filterOnly: { id: 'farm.quarantine.filter.only', defaultMessage: 'только {status}', description: 'Чип активного фильтра: оставлен один статус на карте; {status} — название статуса строчными («красный»)' },
  filterFocus: { id: 'farm.quarantine.filter.focus', defaultMessage: 'на карте: контур № {no}', description: 'Чип активного фильтра: контур выделен на карте; {no} — номер контура' },
  loadWhat: { id: 'farm.quarantine.loadWhat', defaultMessage: 'карантин хозяйства', description: 'Что не загрузилось (в винительном падеже): «Не удалось загрузить карантин хозяйства»' },
  idle: { id: 'farm.quarantine.idle', defaultMessage: 'статусы на {date} (дата экрана), все контуры хозяйства', description: 'Строка «Показано» над вкладкой «Карантин», когда фильтров нет; {date} — дата среза экрана' },
  kpiRed: { id: 'farm.quarantine.kpi.red', defaultMessage: 'Красный', description: 'Подпись KPI-карточки на вкладке «Карантин»: число контуров в красном статусе; коротко' },
  kpiUnitContours: { id: 'farm.quarantine.kpi.unitContours', defaultMessage: 'конт.', description: 'Единица у числа в KPI-карточках «Красный»/«Жёлтый»: сокращение от «контуров»; очень коротко' },
  kpiRedNote: { id: 'farm.quarantine.kpi.redNote', defaultMessage: 'критичное состояние контура', description: 'Нижняя строка KPI «Красный» на вкладке «Карантин»' },
  kpiRedAction: { id: 'farm.quarantine.kpi.redAction', defaultMessage: 'Показать красные на карте', description: 'Подсказка нажимаемой KPI-карточки «Красный»: оставить на карте только красные контуры' },
  kpiYellow: { id: 'farm.quarantine.kpi.yellow', defaultMessage: 'Жёлтый', description: 'Подпись KPI-карточки на вкладке «Карантин»: число контуров в жёлтом статусе; коротко' },
  kpiYellowNote: { id: 'farm.quarantine.kpi.yellowNote', defaultMessage: 'под наблюдением из {total}', description: 'Нижняя строка KPI «Жёлтый»; {total} — всего контуров хозяйства, число' },
  kpiYellowAction: { id: 'farm.quarantine.kpi.yellowAction', defaultMessage: 'Показать жёлтые на карте', description: 'Подсказка нажимаемой KPI-карточки «Жёлтый»: оставить на карте только жёлтые контуры' },
  kpiSignal: { id: 'farm.quarantine.kpi.signal', defaultMessage: 'Под сигналом', description: 'Подпись KPI-карточки: площадь земли хозяйства под карантинным сигналом, га; коротко' },
  kpiSignalWaiting: { id: 'farm.quarantine.kpi.signalWaiting', defaultMessage: 'обработали, ждём контроля: {count}', description: 'Нижняя строка KPI «Под сигналом»; {count} — число обработанных участков, ждущих контроля' },
  kpiSignalPart: { id: 'farm.quarantine.kpi.signalPart', defaultMessage: 'только части контуров хозяйства', description: 'Нижняя строка KPI «Под сигналом»: площадь считается по частям контуров, а не по контурам целиком' },
  kpiReq: { id: 'farm.quarantine.kpi.req', defaultMessage: 'Предписания открыты', description: 'Подпись KPI-карточки на вкладке «Карантин»: число открытых предписаний' },
  kpiReqNote: { id: 'farm.quarantine.kpi.reqNote', defaultMessage: 'после срока {overdue} · устранено {done}', description: 'Нижняя строка KPI «Предписания открыты»; {overdue} — после срока, {done} — устранено, числа' },
  prescriptions: { id: 'farm.quarantine.prescriptions', defaultMessage: 'Предписания хозяйства', description: 'Заголовок блока предписаний на вкладке «Карантин» хозяйства' },
  mapTitle: { id: 'farm.quarantine.map.title', defaultMessage: 'Карта карантина хозяйства', description: 'Заголовок карточки с картой на вкладке «Карантин»' },
  mapReading: { id: 'farm.quarantine.map.reading', defaultMessage: 'читаем дату…', description: 'Индикатор в шапке карточки карты: сервер перечитывает статусы на выбранную дату; коротко' },
  mapCaption: { id: 'farm.quarantine.map.caption', defaultMessage: 'Только контуры хозяйства · статус на {date}', description: 'Подпись под картой карантина хозяйства; {date} — дата статусов' },
  legendLabel: { id: 'farm.quarantine.legend.label', defaultMessage: 'Статусы карантина', description: 'aria-label легенды-фильтра карты (список статусов)' },
  legendFoot: { id: 'farm.quarantine.legend.foot', defaultMessage: 'Ползунок под картой — история: статусы на выбранную неделю. Нажатие контура открывает его карточку, оттуда — карантинная история.', description: 'Пояснение под легендой карты карантина хозяйства' },
  hotTitle: { id: 'farm.quarantine.hot.title', defaultMessage: 'Очаги и предписания', description: 'Заголовок карточки списка контуров в красном/жёлтом статусе с предписаниями' },
  hotNoneTitle: { id: 'farm.quarantine.hot.noneTitle', defaultMessage: 'Очагов нет', description: 'Заголовок пустого состояния карточки «Очаги и предписания»' },
  hotNoneReason: { id: 'farm.quarantine.hot.noneReason', defaultMessage: 'на {date} все контуры хозяйства зелёные', description: 'Пояснение пустого состояния «Очагов нет»; {date} — дата' },
  hotContour: { id: 'farm.quarantine.hot.contour', defaultMessage: 'Контур № {no}', description: 'Ссылка в строке очага: контур с номером; {no} — номер контура' },
  hotSince: { id: 'farm.quarantine.hot.since', defaultMessage: 'с {date}', description: 'Подпись очага: статус действует с даты; {date} — дата' },
  hotNoDate: { id: 'farm.quarantine.hot.noDate', defaultMessage: 'дата не названа', description: 'Подпись очага: дата начала статуса не указана' },
  hotReq: { id: 'farm.quarantine.hot.req', defaultMessage: '{problem} · предписание: срок {deadline}', description: 'Подпись очага; {problem} — «вредители: тля», {deadline} — дата срока или «не назначен»' },
  hotReqLate: { id: 'farm.quarantine.hot.reqLate', defaultMessage: '{problem} · предписание: срок {deadline} — после срока', description: 'Подпись очага с просроченным предписанием; {problem} — «вредители: тля», {deadline} — дата срока' },
  hotNoReq: { id: 'farm.quarantine.hot.noReq', defaultMessage: 'открытого предписания нет', description: 'Подпись очага: открытых предписаний нет' },
  hotDeadlineNone: { id: 'farm.quarantine.hot.deadlineNone', defaultMessage: 'не назначен', description: 'Значение срока предписания, когда срок не назначен (встаёт в место даты); строчными' },
  hotFocus: { id: 'farm.quarantine.hot.focus', defaultMessage: 'Показать на карте', description: 'Всплывающая подсказка кнопки-значка у очага: выделить контур на карте' },
  noProblem: { id: 'farm.quarantine.noProblem', defaultMessage: 'проблема не названа', description: 'Название находки, когда проблема не указана; строчными, после «вредители: »' },
  directionProblem: { id: 'farm.quarantine.directionProblem', defaultMessage: '{direction}: {problem}', description: 'Находка в карантине: направление и проблема, например «вредители: тля»' },
  monthsTitle: { id: 'farm.quarantine.months.title', defaultMessage: 'Карантин по месяцам', description: 'Заголовок карточки с графиками карантина по месяцам' },
  monthsAside: { id: 'farm.quarantine.months.aside', defaultMessage: 'на конец месяца, до {date}', description: 'Подпись справа в шапке карточки «Карантин по месяцам»; {date} — граница периода' },
  monthsRedYellow: { id: 'farm.quarantine.months.redYellow', defaultMessage: 'Контуры в жёлтом и красном', description: 'Подзаголовок графика: число контуров в жёлтом и красном статусе на конец месяца' },
  monthsRedYellowChart: { id: 'farm.quarantine.months.redYellowChart', defaultMessage: 'Контуры хозяйства в жёлтом и красном на конец месяца', description: 'Текстовое описание графика (aria-label) для чтения диктором' },
  monthsEndOf: { id: 'farm.quarantine.months.endOf', defaultMessage: 'Конец месяца {month}', description: 'Заголовок подсказки столбика графика; {month} — месяц вида «03.26»' },
  monthsInspections: { id: 'farm.quarantine.months.inspections', defaultMessage: 'Обследования', description: 'Подзаголовок графика: число карантинных обследований по месяцам' },
  monthsInspectionsChart: { id: 'farm.quarantine.months.inspectionsChart', defaultMessage: 'Обследования контуров хозяйства по месяцам', description: 'Текстовое описание графика обследований (aria-label) для диктора' },
  monthsMonth: { id: 'farm.quarantine.months.month', defaultMessage: 'Месяц {month}', description: 'Заголовок подсказки столбика графика обследований; {month} — месяц вида «03.26»' },
  findingsTitle: { id: 'farm.quarantine.findings.title', defaultMessage: 'Находки, предписания и сроки', description: 'Заголовок карточки с таблицей находок карантина хозяйства' },
  findingsNoneTitle: { id: 'farm.quarantine.findings.noneTitle', defaultMessage: 'Находок нет', description: 'Заголовок пустой таблицы находок' },
  findingsNoneReason: { id: 'farm.quarantine.findings.noneReason', defaultMessage: 'обследования контуров хозяйства находок не дали — до даты среза', description: 'Пояснение пустой таблицы находок' },
  eventsTitle: { id: 'farm.quarantine.events.title', defaultMessage: 'Смена статусов', description: 'Заголовок карточки со списком событий смены карантинных статусов' },
  eventsCount: { id: 'farm.quarantine.events.count', defaultMessage: '{count, plural, one {# событие} few {# события} many {# событий} other {# события}}', description: 'Число событий смены статуса — подпись справа в шапке карточки «Смена статусов»' },
  eventsNone: { id: 'farm.quarantine.events.none', defaultMessage: 'Событий статуса нет — все контуры зелёные по умолчанию.', description: 'Пустое состояние карточки «Смена статусов»' },
  colDate: { id: 'farm.quarantine.col.date', defaultMessage: 'Обследование', description: 'Колонка таблицы находок: дата обследования' },
  colContour: { id: 'farm.quarantine.col.contour', defaultMessage: 'Контур', description: 'Колонка таблицы находок: контур' },
  contourNo: { id: 'farm.quarantine.contourNo', defaultMessage: '№ {no}', description: 'Ссылка на контур в таблице и списке: номер контура' },
  colWhat: { id: 'farm.quarantine.col.what', defaultMessage: 'Находка', description: 'Колонка таблицы находок: что найдено' },
  whatSignal: { id: 'farm.quarantine.what.signal', defaultMessage: 'под сигналом {area}', description: 'Часть строки находки; {area} — площадь под сигналом с единицей («2,5 га»)' },
  whatDemo: { id: 'farm.quarantine.what.demo', defaultMessage: 'синтетика', description: 'Пометка находки, созданной как демонстрационные данные; строчными' },
  colReq: { id: 'farm.quarantine.col.req', defaultMessage: 'Предписание и срок', description: 'Колонка таблицы находок: предписание и срок' },
  reqNone: { id: 'farm.quarantine.req.none', defaultMessage: 'предписания нет', description: 'Значение колонки «Предписание и срок», когда предписания нет' },
  reqIssued: { id: 'farm.quarantine.req.issued', defaultMessage: 'выдано {issued}, срок {deadline}', description: 'Строка предписания; {issued} — дата выдачи, {deadline} — дата срока или «не назначен»' },
  reqEliminated: { id: 'farm.quarantine.req.eliminated', defaultMessage: 'устранено {date}', description: 'Часть строки предписания; {date} — дата устранения по замеру инспектора' },
  reqFollowups: { id: 'farm.quarantine.req.followups', defaultMessage: 'повторный контроль: {list}', description: 'Часть строки предписания; {list} — список «дата — итог» через точку с запятой' },
  reqFollowup: { id: 'farm.quarantine.req.followup', defaultMessage: '{date} — {outcome}', description: 'Один повторный контроль в списке; {date} — дата, {outcome} — итог («устранена»)' },
});

// Конфиги графиков строятся при рендере: подписи берутся на языке интерфейса.
function qConfig(t: ReturnType<typeof useT>): ChartConfig {
  return {
    // i18n-ignore: склонения единицы для подсказки графика (тип ChartConfig из ui/chart/Chart, ещё не на каталоге)
    yellow: { label: t(m.chYellow), token: '--data-warning', forms: ['контур', 'контура', 'контуров'] },
    // i18n-ignore: склонения единицы для подсказки графика (тип ChartConfig из ui/chart/Chart, ещё не на каталоге)
    red: { label: t(m.chRed), token: '--data-critical', forms: ['контур', 'контура', 'контуров'] },
  };
}
function inspConfig(t: ReturnType<typeof useT>): ChartConfig {
  return {
    // i18n-ignore: склонения единицы для подсказки графика (тип ChartConfig из ui/chart/Chart, ещё не на каталоге)
    inspections: { label: t(m.chInspections), token: '--data-series-1', forms: ['обследование', 'обследования', 'обследований'] },
  };
}

const STATUS_META: Record<QStatus, { label: PlainMessage; note: PlainMessage; swatch: string; layer: QuarantineStatusValue }> = {
  red: { label: m.stRed, note: m.stRedNote, swatch: '--data-critical', layer: 'red' },
  yellow: { label: m.stYellow, note: m.stYellowNote, swatch: '--st-warn', layer: 'watch' },
  green: { label: m.stGreen, note: m.stGreenNote, swatch: '--st-ok', layer: 'green' },
};
const ORDER: QStatus[] = ['red', 'yellow', 'green'];

export function FarmQuarantineView({ data, farmId }: { data: FarmCabinetData; farmId: number }) {
  const { ctx, asOf } = data;
  const t = useT();
  const qCfg = qConfig(t);
  const inspCfg = inspConfig(t);
  const sourceKind = useDataSource().current.kind;
  /** Дата ползунка; `undefined` — дата экрана. */
  const [at, setAt] = useState<string | undefined>(undefined);
  const [focus, setFocus] = useState<number | null>(null);
  const [only, setOnly] = useState<Set<QStatus>>(new Set());
  const local = useRequest(
    () => (at ? request<FarmQuarantine>(`/farms/${farmId}/quarantine`, { ...ctx.params, as_of: at }) : Promise.resolve(null)),
    [at, farmId, ctx.key],
  );
  const q = at ? local : data.quarantine;
  const view = q.data ?? (at ? data.quarantine.data : null);
  const pending = at !== undefined && local.state === 'loading';

  const statuses = useMemo(() => view?.statuses ?? [], [view]);
  const quarantineById = useMemo(() => {
    const out: Record<string, QuarantineStatusValue> = {};
    for (const s of statuses) out[String(s.contour_id)] = STATUS_META[(s.status as QStatus) in STATUS_META ? (s.status as QStatus) : 'green'].layer;
    return out;
  }, [statuses]);
  // Сколько контуров в каком статусе — считает сервер (`now`, S4): клиент не складывает.
  const counts: Record<QStatus, number> = {
    red: view?.now.red ?? 0,
    yellow: view?.now.yellow ?? 0,
    green: view?.now.green ?? 0,
  };
  const allIds = useMemo(() => statuses.map((s) => String(s.contour_id)), [statuses]);
  const shownIds = useMemo(() => statuses.filter((s) => only.size === 0 || only.has(s.status as QStatus)).map((s) => String(s.contour_id)), [statuses, only]);

  /** Открытое предписание контура: самый ранний срок среди неустранённых. */
  const openReq = useMemo(() => {
    const byContour = new Map<number, { deadline: string | null; overdue: boolean; problem: string }>();
    for (const f of view?.findings ?? []) {
      for (const r of f.requirements) {
        if (r.state !== 'open' && r.state !== 'overdue') continue;
        const prev = byContour.get(f.contour_id);
        if (!prev || (r.deadline && (!prev.deadline || r.deadline < prev.deadline))) {
          byContour.set(f.contour_id, { deadline: r.deadline, overdue: r.state === 'overdue', problem: t(m.directionProblem, { direction: Q_DIRECTION_LABEL[f.direction] ?? f.direction, problem: f.problem_name ?? t(m.noProblem) }) });
        }
      }
    }
    return byContour;
  }, [view, t]);
  // Земля под сигналом хозяйства и «обработали, ждём контроля» — метрики слоя с
  // фильтром хозяйства (те же, что у ИИ): уникальные га единиц, наибольшая на единицу.
  const farmMetrics = useRequest(
    () =>
      request<{ metrics: MetricValue[] }>('/ai/metrics', {
        ...ctx.params,
        ...(at ? { as_of: at } : {}),
        actor_id: String(farmId),
        ids: 'quarantine.signal_area_ha,quarantine.treated_awaiting_control',
      }),
    [at, farmId, ctx.key],
  );
  const fm = useMemo(() => metricMap(farmMetrics.data?.metrics), [farmMetrics.data]);
  const signalHa = metricNumber(fm, 'quarantine.signal_area_ha');
  const waitingControl = metricNumber(fm, 'quarantine.treated_awaiting_control') ?? 0;
  const hot = statuses.filter((s) => s.status !== 'green').sort((a, b) => (a.status === b.status ? a.contour_no.localeCompare(b.contour_no, 'ru', { numeric: true }) : a.status === 'red' ? -1 : 1));

  const firstEvent = view?.events.length ? view.events[view.events.length - 1]!.effective_on : null;
  const timeline = useMemo<MapTimelineProps | undefined>(() => {
    if (sourceKind !== 'api') return undefined;
    const startDefault = `${Number(asOf.slice(0, 4)) - 1}${asOf.slice(4)}`;
    const from = weekAlignedFrom(firstEvent && firstEvent < startDefault ? firstEvent : startDefault, asOf);
    return {
      from,
      to: asOf,
      value: at ?? null,
      onChange: (next) => setAt(next === null || next === asOf ? undefined : next),
      step: 'week',
      label: t(m.timelineLabel),
    };
  }, [sourceKind, asOf, at, firstEvent, t]);

  const filters: ActiveFilterItem[] = [];
  if (at) filters.push({ key: 'at', text: t(m.filterAt, { date: dateShort(at) }), onClear: () => setAt(undefined) });
  for (const s of only) filters.push({ key: `s-${s}`, text: t(m.filterOnly, { status: Q_STATUS_LABEL[s] ?? s }), onClear: () => setOnly(toggled(only, s)) });
  if (focus !== null) {
    const row = statuses.find((s) => s.contour_id === focus);
    filters.push({ key: 'focus', text: t(m.filterFocus, { no: row?.contour_no ?? focus }), onClear: () => setFocus(null) });
  }

  if (failed(q) && !view) return <LoadError what={t(m.loadWhat)} error={q.error} onRetry={q.reload} />;
  if (!view) return <QuarantineSkeleton />;

  const months = view.months.map((m) => ({ ...m, label: `${m.month.slice(5)}.${m.month.slice(2, 4)}` }));
  const qTicks = niceTicks(Math.max(4, ...months.map((m) => m.yellow + m.red)));
  const iTicks = niceTicks(Math.max(1, ...months.map((m) => m.inspections)));
  const openTotal = (view.requirements.open ?? 0) + (view.requirements.overdue ?? 0);

  // Фильтр легенды — панель управления карты: в развёрнутой карте он стоит
  // её боковой панелью (журнал №1390).
  const legendFilter = (
    <LegendFilter
      label={t(m.legendLabel)}
      rows={ORDER.map((s) => ({ key: s, label: t(STATUS_META[s].label), note: t(STATUS_META[s].note), swatch: STATUS_META[s].swatch, count: counts[s] }))}
      selected={only}
      onToggle={(s) => setOnly(toggled(only, s))}
      unit={(n) => t(m.contourWord, { count: n })}
      foot={t(m.legendFoot)}
    />
  );

  return (
    <Stack>
      <ActiveFilter
        items={filters}
        onReset={() => { setAt(undefined); setOnly(new Set()); setFocus(null); }}
        idle={t(m.idle, { date: dateShort(asOf) })}
      />
      <div data-guide="quarantine">
        <Grid cols={4}>
          <KpiCard label={t(m.kpiRed)} value={num(counts.red)} unit={t(m.kpiUnitContours)} tone={counts.red ? 'warning' : undefined} note={t(m.kpiRedNote)} onClick={() => setOnly(new Set(['red']))} actionLabel={t(m.kpiRedAction)} pressed={only.size === 1 && only.has('red')} />
          <KpiCard label={t(m.kpiYellow)} value={num(counts.yellow)} unit={t(m.kpiUnitContours)} tone={counts.yellow ? 'warning' : undefined} note={t(m.kpiYellowNote, { total: num(view.contours) })} onClick={() => setOnly(new Set(['yellow']))} actionLabel={t(m.kpiYellowAction)} pressed={only.size === 1 && only.has('yellow')} />
          <KpiCard label={t(m.kpiSignal)} value={signalHa === null ? '—' : landHaNum(signalHa)} unit={signalHa === null ? undefined : t(UNIT_LABELS.ha)} note={waitingControl ? t(m.kpiSignalWaiting, { count: num(waitingControl) }) : t(m.kpiSignalPart)} />
          <KpiCard label={t(m.kpiReq)} value={num(openTotal)} tone={(view.requirements.overdue ?? 0) > 0 ? 'warning' : undefined} note={t(m.kpiReqNote, { overdue: num(view.requirements.overdue ?? 0), done: num((view.requirements.eliminated ?? 0) + (view.requirements.eliminated_late ?? 0)) })} />
        </Grid>
      </div>
      <div data-guide="quarantine-prescriptions"><PrescriptionsWidget scope={{ actorId: farmId }} title={t(m.prescriptions)} /></div>
      <div className="cab-qsplit">
        <div data-guide="quarantine-map">
          <Card size="lg" title={t(m.mapTitle)} aside={pending ? <span className="cab-why">{t(m.mapReading)}</span> : <Badge tone="neutral">{countOf(view.contours, WORDS.contour)}</Badge>}>
            <div className="cab-mapgrid">
              <AgroMap
                colorBy="quarantine"
                mode="frame"
                height={460}
                legend="collapsed"
                panel={{ title: t(m.legendLabel), content: legendFilter }}
                quarantineById={quarantineById}
                filterIds={(only.size ? shownIds : allIds).length ? (only.size ? shownIds : allIds) : undefined}
                focusIds={focus !== null ? [String(focus)] : allIds.length ? allIds : undefined}
                selectedId={focus !== null ? String(focus) : null}
                timeline={timeline}
                caption={t(m.mapCaption, { date: dateShort(at ?? asOf) })}
              />
              {legendFilter}
            </div>
            <p className="cab-why">{view.default_note}</p>
          </Card>
        </div>
        <div data-guide="quarantine-hotspots">
          <Card size="lg" title={t(m.hotTitle)} aside={<Badge tone="neutral">{countOf(hot.length, WORDS.contour)}</Badge>}>
            {hot.length === 0 ? (
              <EmptyState compact icon={ShieldCheck} title={t(m.hotNoneTitle)} reason={t(m.hotNoneReason, { date: dateShort(at ?? asOf) })} />
            ) : (
              <ul className="cab-hot">
                {hot.map((s) => {
                  const req = openReq.get(s.contour_id);
                  const on = focus === s.contour_id;
                  return (
                    <li key={s.contour_id} className={on ? 'is-on' : undefined}>
                      <span className={`cab-qtag cab-qtag--${s.status}`}>{Q_STATUS_LABEL[s.status] ?? s.status}</span>
                      <span className="cab-hot__body">
                        <Link to={`/contours/${s.contour_id}${ctx.linkSearch({ tab: 'quarantine' })}`}>{t(m.hotContour, { no: s.contour_no })}</Link>
                        <small>
                          {s.since ? t(m.hotSince, { date: dateShort(s.since) }) : t(m.hotNoDate)}
                          {s.reason ? ` · ${s.reason}` : ''}
                        </small>
                        {partNote({ on_part: s.on_part, signal_ha: s.signal_microha != null ? s.signal_microha / 1_000_000 : null, part_unspecified: s.part_unspecified }) ? (
                          <small>{partNote({ on_part: s.on_part, signal_ha: s.signal_microha != null ? s.signal_microha / 1_000_000 : null, part_unspecified: s.part_unspecified })}</small>
                        ) : null}
                        {s.case_state && CASE_STATE_WORD[s.case_state] && s.case_state !== 'no_requirement' ? <small>{CASE_STATE_WORD[s.case_state]}</small> : null}
                        {s.outside_cycle_reason ? <small>{OUTSIDE_CYCLE_WORD[s.outside_cycle_reason] ?? s.outside_cycle_reason}</small> : null}
                        {req ? (
                          <small className={req.overdue ? 'cab-hot__late' : undefined}>
                            {t(req.overdue ? m.hotReqLate : m.hotReq, { problem: req.problem, deadline: req.deadline ? dateShort(req.deadline) : t(m.hotDeadlineNone) })}
                          </small>
                        ) : (
                          <small>{t(m.hotNoReq)}</small>
                        )}
                      </span>
                      <button type="button" className="cab-hot__focus focus-ring" aria-pressed={on} title={t(m.hotFocus)} onClick={() => setFocus(on ? null : s.contour_id)}>
                        <Icon icon={MapPin} size={16} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>
      <div data-guide="quarantine-months">
        <Card title={t(m.monthsTitle)} aside={t(m.monthsAside, { date: dateShort(view.horizon) })}>
          <div className="cab-split cab-split--even">
            <div>
              <strong className="cab-subhead">{t(m.monthsRedYellow)}</strong>
              <ChartContainer config={qCfg} height={180} label={t(m.monthsRedYellowChart)}>
                <BarChart data={months} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barCategoryGap="22%">
                  <CartesianGrid vertical={false} strokeDasharray="3 4" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} />
                  <YAxis width={30} ticks={qTicks} domain={[0, qTicks.at(-1)!]} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: 'var(--data-grid)', fillOpacity: 0.5 }} content={(p) => <ChartTooltipContent {...(p as object)} config={qCfg} title={(l) => t(m.monthsEndOf, { month: String(l) })} />} />
                  <Bar dataKey="yellow" stackId="q" fill="var(--color-yellow)" isAnimationActive={false} />
                  <Bar dataKey="red" stackId="q" fill="var(--color-red)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ChartContainer>
              <ChartLegend config={qCfg} />
            </div>
            <div>
              <strong className="cab-subhead">{t(m.monthsInspections)}</strong>
              <ChartContainer config={inspCfg} height={180} label={t(m.monthsInspectionsChart)}>
                <BarChart data={months} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barCategoryGap="22%">
                  <CartesianGrid vertical={false} strokeDasharray="3 4" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} />
                  <YAxis width={36} ticks={iTicks} domain={[0, iTicks.at(-1)!]} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: 'var(--data-grid)', fillOpacity: 0.5 }} content={(p) => <ChartTooltipContent {...(p as object)} config={inspCfg} title={(l) => t(m.monthsMonth, { month: String(l) })} />} />
                  <Bar dataKey="inspections" fill="var(--color-inspections)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ChartContainer>
              <ChartLegend config={inspCfg} />
            </div>
          </div>
        </Card>
      </div>
      <div data-guide="quarantine-findings">
        <Card title={t(m.findingsTitle)} aside={countOf(view.findings.length, WORDS.finding)}>
          <DataTable columns={findingColumns(ctx.linkSearch, t)} rows={view.findings} getKey={(f) => String(f.id)} denominator={m.findingWord} limit={10} empty={{ title: t(m.findingsNoneTitle), reason: t(m.findingsNoneReason) }} />
        </Card>
      </div>
      <div data-guide="quarantine-events">
        <Card title={t(m.eventsTitle)} aside={countOf(view.events.length, m.eventsCount)}>
          {view.events.length === 0 ? (
            <p className="cab-why">{t(m.eventsNone)}</p>
          ) : (
            <ul className="cab-timeline">
              {view.events.slice(0, 12).map((e, i) => (
                <li key={`${e.contour_id}-${e.effective_on}-${i}`}>
                  <time>{dateShort(e.effective_on)}</time>
                  <span>
                    <Link to={`/contours/${e.contour_id}${ctx.linkSearch({ tab: 'quarantine' })}`}>{t(m.contourNo, { no: e.contour_no })}</Link>{' '}
                    <span className={`cab-qtag cab-qtag--${e.status}`}>{Q_STATUS_LABEL[e.status] ?? e.status}</span>
                    {e.reason ? ` — ${e.reason}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </Stack>
  );
}

function findingColumns(linkSearch: (extra?: Record<string, string | number>) => string, t: ReturnType<typeof useT>): Column<QFinding>[] {
  return [
    { key: 'date', header: t(m.colDate), render: (f) => dateShort(f.occurred_on) },
    { key: 'contour', header: t(m.colContour), render: (f) => <Link to={`/contours/${f.contour_id}${linkSearch({ tab: 'quarantine' })}`}>{t(m.contourNo, { no: f.contour_no })}</Link> },
    {
      key: 'what',
      header: t(m.colWhat),
      render: (f) =>
        [
          t(m.directionProblem, { direction: Q_DIRECTION_LABEL[f.direction] ?? f.direction, problem: f.problem_name ?? t(m.noProblem) }),
          f.object_class ? CLASS_LONG[f.object_class as ObjectClass] ?? f.object_class : null,
          f.signal_microha != null ? t(m.whatSignal, { area: haText(f.signal_microha / 1_000_000) ?? '—' }) : null,
          f.is_demo ? t(m.whatDemo) : null,
        ]
          .filter(Boolean)
          .join(' · '),
    },
    {
      key: 'req',
      header: t(m.colReq),
      render: (f) =>
        f.requirements.length === 0 ? (
          <span className="cab-why">{t(m.reqNone)}</span>
        ) : (
          f.requirements.map((r) => (
            <div key={r.id} className="cab-req">
              <Badge tone={r.state === 'overdue' ? 'copper' : r.state.startsWith('eliminated') ? 'sage' : 'outline'}>{REQ_STATE_LABEL[r.state] ?? r.state}</Badge>
              <span>
                {t(m.reqIssued, { issued: dateShort(r.issued_on), deadline: r.deadline ? dateShort(r.deadline) : t(m.hotDeadlineNone) })}
                {r.eliminated_on ? `, ${t(m.reqEliminated, { date: dateShort(r.eliminated_on) })}` : ''}
                {r.followups.length ? ` · ${t(m.reqFollowups, { list: r.followups.map((x) => t(m.reqFollowup, { date: dateShort(x.occurred_on), outcome: FOLLOWUP_LABEL[x.outcome] ?? x.outcome })).join('; ') })}` : ''}
                {findingWorkText(r.works, Boolean(r.eliminated_on)) ? ` · ${findingWorkText(r.works, Boolean(r.eliminated_on))}` : ''}
              </span>
            </div>
          ))
        ),
    },
  ];
}
